import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

// Always-visible label tied to a native date input (htmlFor + aria-label), so the
// field is never a blank box on iOS/Android. The browser keeps the picked date
// visible in MM/DD/YYYY after the picker closes.
export function LabeledDate({ id, label, value, onChange, max, testid }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs font-medium text-slate-600">{label}</Label>
      <Input id={id} type="date" aria-label={label} value={value || ''} max={max}
             onChange={onChange} data-testid={testid} className="w-full" />
    </div>
  );
}

// File picker that offers the phone's full menu (no `capture`, so Take Photo /
// Photo Library / Choose File all appear). The chosen file is STAGED first — its
// name + thumbnail are shown with Remove / Replace before it is submitted.
export function StagedUpload({ testid, accept, label, onLabelChange, detailsValid,
                              busy, onUpload, chooseLabel, extra }) {
  const inputRef = React.useRef(null);
  const [staged, setStaged] = React.useState(null);

  const pick = (file) => {
    if (!file) return;
    setStaged((s) => {
      if (s?.url) URL.revokeObjectURL(s.url);
      const url = file.type && file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      return { file, name: file.name, url };
    });
  };
  const clear = () => setStaged((s) => { if (s?.url) URL.revokeObjectURL(s.url); return null; });
  const submit = async () => {
    if (!staged) return;
    const ok = await onUpload(staged.file);
    if (ok) clear();
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor={`${testid}-page`} className="text-xs text-slate-600">Page</Label>
        <select id={`${testid}-page`} value={label || 'front'}
                onChange={(e) => onLabelChange(e.target.value)}
                className="h-9 rounded border border-slate-300 px-2 text-sm bg-white text-slate-900"
                data-testid={`${testid}-label`}>
          <option value="front">Front</option>
          <option value="back">Back</option>
          <option value="other">Other page</option>
        </select>
        <input ref={inputRef} type="file" accept={accept} className="hidden"
               data-testid={`${testid}-file`}
               onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        <Button type="button" size="sm" variant="outline" disabled={!detailsValid}
                onClick={() => inputRef.current?.click()} data-testid={`${testid}-choose`}>
          {staged ? 'Choose a different file' : chooseLabel}
        </Button>
        {extra}
      </div>
      {staged && (
        <div className="flex items-center gap-3 rounded border border-slate-200 bg-slate-50 p-2"
             data-testid={`${testid}-staged`}>
          {staged.url
            ? <img src={staged.url} alt="Selected document preview"
                   className="h-14 w-14 rounded object-cover border border-slate-300"
                   data-testid={`${testid}-thumb`} />
            : <div className="flex h-14 w-14 items-center justify-center rounded border border-slate-300 bg-white text-[10px] font-medium text-slate-500">PDF</div>}
          <div className="min-w-0 flex-1 truncate text-xs text-slate-700" data-testid={`${testid}-filename`}>{staged.name}</div>
          <Button type="button" size="sm" variant="ghost" disabled={busy}
                  onClick={clear} data-testid={`${testid}-remove`}>Remove</Button>
          <Button type="button" size="sm" disabled={busy || !detailsValid}
                  onClick={submit} data-testid={`${testid}-upload`}>
            {busy ? 'Uploading…' : 'Upload this file'}
          </Button>
        </div>
      )}
    </div>
  );
}
